import React, { useCallback, useRef } from 'react'
import { createPortal } from 'react-dom'
import { useScroll, useWindowScroll } from 'react-use'

import { Banner, Drawer, Icon, WebViewRemover, useMediaQueryContext } from '@fourthwall/components'
import { BurgerIcon } from '@fourthwall/icons'
import { isWebView } from '@fourthwall/utils'
import { useAIKitDashboard } from '@modules/AIKit/AIKitDashboardProvider'
import { DashboardChatbot } from '@modules/AIKit/Chatbot'
import { DashboardOmnisearch } from '@modules/AIKit/Omnisearch'
import { useMenuDrawerContext } from '@modules/Dashboard/contexts/MenuDrawerContext'

import * as S from './Dashboard.styled'
import { DashboardItemViewHeader } from './DashboardItemViewHeader'
import { DashboardMenu } from './DashboardMenu'
import { DashboardSection } from './DashboardSection'
import { DASHBOARD_STYLED_CONSTS } from './consts'
import type { DashboardProps } from './types'

export const Dashboard = ({
  headerMobile,
  toolbarLeftContentDesktop,
  toolbarCenterContent,
  toolbarRightContent,
  sidebarContent,
  drawerFooterContent,
  bannersProps,
  content,
}: DashboardProps) => {
  const contentRef = useRef<HTMLDivElement>(null)
  const { chatbotOpen, chatbotPortalRoot, omnisearchOpen, omnisearchPortalRoot } =
    useAIKitDashboard()
  const mq = useMediaQueryContext()
  const { drawerActive, drawerRoot, drawerOpen, drawerClose } = useMenuDrawerContext()

  // When chatbot is open, Content has overflow-y: auto and we monitor its scroll
  // When chatbot is closed, the whole window scrolls
  const contentScroll = useScroll(contentRef)
  const windowScroll = useWindowScroll()
  const y = chatbotOpen ? contentScroll.y : windowScroll.y
  const isScrolled = chatbotOpen ? y > 0 : y >= DASHBOARD_STYLED_CONSTS.HEADER_HEIGHT

  const renderHeader = () => {
    if (!mq.laptop) return null

    return (
      <S.Header isScrolled={isScrolled}>
        <S.HeaderSide>{toolbarLeftContentDesktop}</S.HeaderSide>
        <S.ToolbarCenter>{toolbarCenterContent}</S.ToolbarCenter>
        <S.HeaderSide $alignRight>
          {toolbarRightContent && <S.Toolbar>{toolbarRightContent}</S.Toolbar>}
        </S.HeaderSide>
      </S.Header>
    )
  }

  const renderSidebar = () => {
    if (mq.laptop) {
      return (
        !!sidebarContent && (
          <S.Sidebar>
            <S.SidebarInner>{sidebarContent}</S.SidebarInner>
          </S.Sidebar>
        )
      )
    }

    return (
      <WebViewRemover>
        <S.Navbar>
          <S.BurgerButton onClick={drawerOpen}>
            <Icon component={BurgerIcon} width={20} height={20} />
          </S.BurgerButton>

          <S.NavbarActions>{toolbarRightContent}</S.NavbarActions>
          {createPortal(
            <Drawer>
              {drawerActive && (
                <Drawer.Body
                  position="left"
                  width={mq.tablet ? 300 : '100%'}
                  overlay={<Drawer.Overlay onClick={drawerClose} />}
                >
                  <Drawer.Header>{headerMobile}</Drawer.Header>
                  <Drawer.Content>
                    <Drawer.Section>{sidebarContent}</Drawer.Section>
                  </Drawer.Content>
                  <Drawer.Footer>{drawerFooterContent}</Drawer.Footer>
                </Drawer.Body>
              )}
            </Drawer>,
            drawerRoot,
          )}
        </S.Navbar>
      </WebViewRemover>
    )
  }

  const renderBanners = useCallback(() => {
    if (!bannersProps) return

    if (isWebView()) return

    return (
      <S.BannersWrapper>
        {bannersProps.map((bannerProps) => (
          <Banner {...bannerProps} />
        ))}
      </S.BannersWrapper>
    )
  }, [bannersProps])

  return (
    <div>
      {renderHeader()}
      <S.Main id="main">
        {renderSidebar()}
        <S.Body id="body">
          {renderBanners()}
          <S.ContentWithChatWrapper>
            {content && (
              <S.Content ref={contentRef} $isWebView={isWebView()} $chatbotOpen={chatbotOpen}>
                {content}
              </S.Content>
            )}
          </S.ContentWithChatWrapper>
        </S.Body>
      </S.Main>

      {createPortal(
        <S.ChatbotPortalWrapper $isOpen={chatbotOpen}>
          {chatbotOpen && <DashboardChatbot />}
        </S.ChatbotPortalWrapper>,
        chatbotPortalRoot,
      )}

      {createPortal(
        <S.OmnisearchPortalWrapper $isOpen={omnisearchOpen}>
          {omnisearchOpen && <DashboardOmnisearch />}
        </S.OmnisearchPortalWrapper>,
        omnisearchPortalRoot,
      )}
    </div>
  )
}

Dashboard.Menu = DashboardMenu
Dashboard.Section = DashboardSection
Dashboard.ItemViewHeader = DashboardItemViewHeader
